import closeSvg from '@/svg/close_big.svg'

export default class Dialog {
    name = null // Dialog name for events
    modal = false // Dialog without close events
    closeButton = true // Add close button or not
    backdropClose = true

    constructor() {
        this.onDialogShow = this.onDialogShow.bind(this)
        this.onDialogHide = this.onDialogHide.bind(this)
        this.onTransitionEnd = this.onTransitionEnd.bind(this)
        this.onKeyDown = this.onKeyDown.bind(this)
        this.onBackdropMouseDown = this.onBackdropMouseDown.bind(this)
        this.onCloseClick = this.onCloseClick.bind(this)

        document.addEventListener('dialog:show', this.onDialogShow)
        document.addEventListener('dialog:hide', this.onDialogHide)
    }

    onDialogShow(event) {
        const name = event.detail?.name
        if (name === this.name) {
            this.#show(event.detail?.options)
        } else {
            this.#hide()
        }
    }

    onDialogHide(event) {
        const name = event.detail?.name
        if (name === this.name) {
            this.#hide(event.detail?.data)
        } else if (!name) {
            this.#hide()
        }
    }

    onTransitionEnd(event) {
        if (event.propertyName !== 'visibility' || this.el.classList.contains('visible')) return

        this.afterHide()
    }

    onKeyDown(event) {
        if (
            !this.el?.classList.contains('visible') ||
            event.altKey || event.ctrlKey || event.shiftKey || event.metaKey
            // || event.target.matches('input, select, textarea, button, audio, video') // Включить, если появятся другие кнопки
        ) {
            return
        }

        if (event.code === 'Escape') {
            event.preventDefault()

            this.hide()
        }
    }

    onBackdropMouseDown(event) {
        if (!this.backdropClose) {
            return
        }

        // Должен срабатывать только при клике по враперу
        if (event.target !== this.el) return

        event.preventDefault()

        this.hide()
    }

    onCloseClick() {
        this.hide()
    }

    /*
    * Do not call or reload this method!
    */
    #show(options) {
        if (this.el && this.el.classList.contains('visible')) return

        this.options = options

        this.render()

        if (this.beforeShow()) return // Postpone show while waiting for ajax content

        if (!this.modal) {
            document.addEventListener('keydown', this.onKeyDown)
            this.el.addEventListener('mousedown', this.onBackdropMouseDown)
        }

        this.el.classList.add('visible')
        this.contentEl.focus({
            preventScroll: true, // prevent dialog padding top scroll
        })

        this.afterShow()
    }

    /*
    * Do not call or reload this method!
    */
    #hide(data) {
        if (!this.el?.classList.contains('visible')) return

        if (!this.modal) {
            document.removeEventListener('keydown', this.onKeyDown)
            this.el.removeEventListener('mousedown', this.onBackdropMouseDown)
        }

        this.el.classList.remove('visible')

        this.options?.onHide?.(data)
    }

    /**
     * Show any dialog
     * @param  {string}   name  - Dialog name
     * @param  {Object} options - custom options
     */
    static show(name, options) {
        document.dispatchEvent(new CustomEvent('dialog:show', {
            detail: {
                name,
                options,
            },
        }))
    }

    /**
     * Hide any dialog
     * @param  {string} name - Dialog name
     * @param  {object} data - Data object
     */
    static hide(name, data) {
        document.dispatchEvent(new CustomEvent('dialog:hide', {
            detail: {
                name,
                data,
            },
        }))
    }

    /**
     * Show current dialog
     * @param  {Object} options - custom options
     */
    show(options) {
        Dialog.show(this.name, options)
    }

    /**
     * Hide current dialog
     * @param  {object} data - Data object
     */
    hide(data) {
        Dialog.hide(this.name, data)
    }

    /**
     * Method called before the dialog is shown
     * @return {bool} true to postpone show while waiting for ajax content
     *                Dialog instance should call show method again when loading is complete
     */
    beforeShow() {}

    /**
     * Method called after the dialog is shown
     */
    afterShow() {}

    /**
     * Method called after the dialog is hidden
     */
    afterHide() {}

    /**
     * Render dialog template
     * @param  {string} template - HTML template
     * @return {bool} false if dialog has been rendered before
     */
    render(template) {
        if (this.el) return

        this.el = document.createElement('div')
        this.el.className = `dialog dialog_${this.name}`
        this.el.insertAdjacentHTML('beforeend', '<div class="dialog__content" tabindex="0"></div>')

        this.contentEl = this.el.querySelector('.dialog__content')

        if (!this.modal && this.closeButton) {
            this.closeEl = document.createElement('button')
            this.closeEl.className = 'dialog__close'
            this.closeEl.insertAdjacentHTML('afterbegin', `
                <svg class="svg svg_close">
                    <use xlink:href="${closeSvg}"></use>
                </svg>
            `)

            this.el.insertAdjacentElement('beforeend', this.closeEl)

            this.closeEl.addEventListener('click', this.onCloseClick)
        }

        if (template) {
            if (template instanceof HTMLElement) {
                this.contentEl.append(template)
            } else if (typeof template === 'string') {
                this.contentEl.insertAdjacentHTML('beforeend', template)
            }
        }

        document.body.appendChild(this.el)

        this.el.addEventListener('transitionend', this.onTransitionEnd)

        return true
    }
}
